import cloneDeep from 'lodash/cloneDeep';

import {
  CustomEventTarget,
  getObservableObjectKeyPath,
  makeObservableObject,
  matchObservableObjectChange,
  ObservableObjectChange,
} from '@libs/util-ts';
import {
  type VideoDescriptor as PMVideoDescriptor,
  type SerializedVideoDescriptor,
} from '@libs/waymark-video/video-descriptor-types';
import { FontAsset, FootageAsset, ImageAsset, VideoDescriptorSource, AudioAsset } from './assets';
import { SceneSwitch } from './switches';
import { Composition } from './compositions';
import { ColorReference, FontReference, ImageReference, TextReference } from './references';

import { runAllMigrations, MigrationContext } from './migrations/migrationEngine';
import type { AnyVideoDescriptor } from './migrations/types';
import { templateBundleService } from './utils/TemplateBundleService';

import {
  AudioLayer,
  FootageLayer,
  ImageLayer,
  TextLayer,
  type VideoDescriptorLayer,
} from './layers';
import { VideoDescriptorTimeline } from './timeline';
import { makeAsset } from './assets/makeAsset';

// TODO: Allow creation of empty VideoDecriptor?
export class VideoDescriptor extends CustomEventTarget<{
  layerAdded: VideoDescriptorLayer;
  layerRemoved: VideoDescriptorLayer;
  assetRemoved: VideoDescriptorSource;
  compositionRemoved: Composition;
  // Event emitted every time a change is made to the underlying raw video descriptor data.
  // This event is debounced to avoid emitting a fire-hose of events as a change is made.
  change: ObservableObjectChange;
}> {
  // Using an assignment assertion here because TS doesn't know that this value should only be
  // set via the rawData setter, which does get used in the constructor.
  private _rawData!: PMVideoDescriptor;

  /**
   * Queue which we will use to process video descriptor data change events in order.
   */
  private _changeEventQueue: ObservableObjectChange[] = [];
  private _isProcessingChangeEvent = false;

  get rawData(): PMVideoDescriptor {
    return this._rawData;
  }

  readonly rootCompID: string;
  compositions: Map<string, Composition>;

  switches: SceneSwitch[];

  // Map of asset IDs to asset instances
  private _assetCache: Map<string, ImageAsset | FootageAsset | FontAsset | AudioAsset>;

  // Map of layer UUIDs to layer instances
  private _layerCache: Map<string, VideoDescriptorLayer>;

  // Maps reference IDs to reference instances, grouped by type
  // Grouping by type makes typings easier and allows simpler lookup of all references of a given type
  references: {
    text: Record<string, TextReference>;
    image: Record<string, ImageReference>;
    color: Record<string, ColorReference>;
    font: Record<string, FontReference>;
  };

  timeline: VideoDescriptorTimeline;

  static async from(
    serialized: SerializedVideoDescriptor,
    migrationContext?: MigrationContext,
  ): Promise<VideoDescriptor> {
    let videoDescriptorData: AnyVideoDescriptor;
    if ('templateSlug' in serialized && 'configuration' in serialized) {
      const bundleJson = await templateBundleService.getTemplateBundle(serialized.templateSlug);

      if (!bundleJson) {
        throw new Error(`Could not retrieve bundle ${serialized.templateSlug}`);
      }

      videoDescriptorData = {
        projectManifest: bundleJson.projectManifest,
        templateManifest: bundleJson.templateManifest,
        __activeConfiguration: serialized.configuration ?? bundleJson.placeholderConfiguration,
        __templateSlug: serialized.templateSlug,
      };
    } else {
      videoDescriptorData = serialized;
    }
    return new VideoDescriptor(await runAllMigrations(videoDescriptorData, migrationContext));
  }

  toJSON(): SerializedVideoDescriptor {
    // Deeply clone to break free of the proxy references created by makeObservableObject
    return cloneDeep(this.rawData);
  }

  toJSONString(): string {
    return JSON.stringify(this.rawData);
  }

  constructor(serialized: PMVideoDescriptor) {
    super();

    this._layerCache = new Map();
    this.addEventListener('layerRemoved', (evt) => {
      const removedLayer = evt.detail;
      this._layerCache.delete(removedLayer.getUUID());
    });
    this.addEventListener('layerAdded', (evt) => {
      const addedLayer = evt.detail;
      this._layerCache.set(addedLayer.getUUID(), addedLayer);
    });

    this._assetCache = new Map();

    // Deeply proxy the raw data object so we can emit a `change` event any time it is modified.
    // This is mainly used for the editor to know when to save changes to the DB
    this._rawData = makeObservableObject(serialized, (change) => {
      // Use a queue to ensure change events are processed in order, one at a time
      // to avoid weirdness if a change handler performs a side-effect that modifies the
      // video descriptor. Otherwise, our undo stack can end up out of order and cause things to break.
      this._changeEventQueue.push(change);

      if (this._isProcessingChangeEvent) {
        return;
      }

      this._isProcessingChangeEvent = true;

      try {
        let nextChange: ObservableObjectChange | undefined;
        while ((nextChange = this._changeEventQueue.shift())) {
          this.dispatchEvent('change', nextChange);
        }
      } finally {
        this._isProcessingChangeEvent = false;
      }
    });

    // Listen for changes to the assets array in the project manifest
    // and update the asset cache accordingly
    this.addEventListener('change', (event) => {
      const assetsArrayKeyPath = getObservableObjectKeyPath(this.rawData.projectManifest.assets);
      if (!assetsArrayKeyPath) {
        return;
      }

      const matchingAssetChange = matchObservableObjectChange(event.detail, assetsArrayKeyPath);
      if (matchingAssetChange) {
        this.onAssetsArrayChanged();
      }
    });

    /**
     * NOTE: the order of how we initialize everything in this constructor is very important!
     * Switches depend on layers (created by Compositions), which may depend on references, which may depend on assets.
     * So we need to make sure the order of creation is always:
     * 1. References
     * 2. Compositions
     * 3. Switches
     *
     * (assets are created and cached on demand when getAssetByID is called,
     *  so we don't need to do any setup for those up front)
     */

    this.references = {
      text: {},
      color: {},
      image: {},
      font: {},
    };

    for (const rawReferenceData of this.rawData.templateManifest.overrides ?? []) {
      switch (rawReferenceData.type) {
        case 'text':
          this.references.text[rawReferenceData.id] = new TextReference(rawReferenceData, this);
          break;
        case 'image':
          this.references.image[rawReferenceData.id] = new ImageReference(rawReferenceData, this);
          break;
        case 'color':
          this.references.color[rawReferenceData.id] = new ColorReference(rawReferenceData, this);
          break;
        case 'font':
          this.references.font[rawReferenceData.id] = new FontReference(rawReferenceData, this);
          break;
      }
    }

    this.compositions = new Map();
    const rootComposition = new Composition(this.rawData.projectManifest, this);

    this.rootCompID = rootComposition.id;

    this.compositions.set(this.rootCompID, rootComposition);

    const templateManifest = this.rawData.templateManifest;

    this.switches =
      templateManifest.sceneSwitches?.map(
        (sceneSwitchData) => new SceneSwitch(sceneSwitchData, this),
      ) ?? [];

    this.timeline = new VideoDescriptorTimeline(this);
  }

  onAssetsArrayChanged() {
    const removedAssetIDs = new Set(this._assetCache.keys());

    for (const asset of this.rawData.projectManifest.assets) {
      if (!asset) {
        continue;
      }
      const assetID = asset.id;
      removedAssetIDs.delete(assetID);
      const cachedAsset = this._assetCache.get(assetID);
      if (cachedAsset) {
        // Update the cached asset with the new raw asset data
        cachedAsset.rawAssetData = asset as any;
      }
    }

    for (const removedAssetID of removedAssetIDs) {
      // If the asset was removed, we need to remove it from the cache
      this._assetCache.delete(removedAssetID);
    }
  }

  /**
   * Applies a raw video descriptor in place to this video descriptor.
   * This can be used to update the video descriptor with a newly generated one from autofill.
   */
  applyRawVideoDescriptorData(newRawData: PMVideoDescriptor) {
    // Just apply the whole new raw data object.
    // This will trigger a change handler on every VideoDescriptor entity (ie layers, compositions, references)
    // and they will update themselves accordingly. If any data got moved around/references were broken,
    // layers and compositions will attempt to heal themselves to correct this.
    Object.assign(this.rawData, newRawData);
  }

  getRootComposition(): Composition {
    const rootComposition = this.compositions.get(this.rootCompID);
    if (!rootComposition) {
      throw new Error(`Root composition with ID ${this.rootCompID} not found in video descriptor`);
    }
    return rootComposition;
  }

  removeComposition(composition: Composition) {
    this.compositions.delete(composition.id);
    this.dispatchEvent('compositionRemoved', composition);
  }

  getFramerate() {
    return this.rawData.projectManifest.fr;
  }

  getDimensions() {
    return {
      width: this.rawData.projectManifest.w,
      height: this.rawData.projectManifest.h,
    };
  }

  *layersIterator(): IterableIterator<VideoDescriptorLayer> {
    for (const composition of this.compositions.values()) {
      yield* composition.layers;
    }
  }

  /**
   * Finds a layer matching a predicate.
   */
  findLayer<TPredicateLayerType extends VideoDescriptorLayer>(
    predicate:
      | ((layer: VideoDescriptorLayer) => layer is TPredicateLayerType)
      | ((layer: VideoDescriptorLayer) => boolean),
  ): TPredicateLayerType | undefined {
    for (const layer of this.layersIterator()) {
      if (predicate(layer)) {
        return layer as TPredicateLayerType;
      }
    }

    return undefined;
  }

  /**
   * Finds a layer in the video descriptor by UUID or by a predicate function.
   * @param {string} uuid - The UUID of the layer to find
   */
  findLayerByUUID(uuid: string): VideoDescriptorLayer | undefined {
    const cachedLayer = this._layerCache.get(uuid);
    if (cachedLayer) {
      return cachedLayer;
    }

    const layer = this.findLayer((layer) => layer.getUUID() === uuid);
    if (layer) {
      this._layerCache.set(uuid, layer);
    }

    return layer;
  }

  /**
   * Runs a callback for every layer from all compositions in the video descriptor.
   */
  forEachLayer(callback: (layer: VideoDescriptorLayer) => void) {
    for (const layer of this.layersIterator()) {
      callback(layer);
    }
  }

  getAllLayers(): VideoDescriptorLayer[] {
    // Gather all layers from the root composition and all sub-compositions into a single array
    // Using a set to de-duplicate layers
    const layerSet = new Set<VideoDescriptorLayer>();
    this.forEachLayer((layer) => {
      layerSet.add(layer);
    });

    return Array.from(layerSet);
  }

  /**
   * Takes a predicate callback and returns all layers that match the predicate.
   * If you include a type predicate on the filter callback, TypeScript will infer the correct type for the returned layers.
   */
  filterLayers<TPredicateLayerType extends VideoDescriptorLayer>(
    predicate:
      | ((layer: VideoDescriptorLayer) => layer is TPredicateLayerType)
      | ((layer: VideoDescriptorLayer) => boolean),
  ): TPredicateLayerType[] {
    const layerSet = new Set<TPredicateLayerType>();

    this.forEachLayer((layer) => {
      if (predicate(layer)) {
        layerSet.add(layer as TPredicateLayerType);
      }
    });

    return Array.from(layerSet);
  }

  /**
   * Registers an asset in the VideoDescriptor. This is necessary to run every time a new asset is
   * set on a layer to ensure the layer can safely reference that asset.
   */
  addAsset(asset: VideoDescriptorSource) {
    const assetID = asset.getID();

    this._assetCache.set(assetID, asset);

    const { assets } = this.rawData.projectManifest;

    const assetIndex = assets.findIndex((asset) => asset?.id === assetID);

    if (assetIndex === -1) {
      assets.push(asset.rawAssetData);
    } else {
      assets[assetIndex] = asset.rawAssetData;
    }
  }

  /**
   * Removes an asset from the VideoDescriptor. This is necessary to run every time an asset is
   * removed/replaced on a layer to avoid memory leaks.
   * If the asset is still referenced by another layer, this method will do nothing.
   */
  removeAsset(asset: VideoDescriptorSource) {
    const assetID = asset.getID();

    // Determine if the asset is still being used by any layers and skip removal if so
    if (asset instanceof ImageAsset) {
      // Make sure no image references or image layers are using this image asset
      for (const imageReferences of Object.values(this.references.image)) {
        if (imageReferences.getImageAsset() === asset) {
          return;
        }
      }

      if (
        this.findLayer((layer) => layer instanceof ImageLayer && layer.getAssetID() === assetID)
      ) {
        return;
      }
    } else if (asset instanceof FootageAsset) {
      // Make sure no footage layers are using this footage asset
      if (
        this.findLayer((layer) => layer instanceof FootageLayer && layer.getAssetID() === assetID)
      ) {
        return;
      }
    } else if (asset instanceof FontAsset) {
      // Make sure no font references or text layers are using this font asset
      for (const fontReferences of Object.values(this.references.font)) {
        if (fontReferences.getFontAssetID() === asset.getID()) {
          return;
        }
      }
      if (
        this.findLayer((layer) => layer instanceof TextLayer && layer.getFontAssetID() === assetID)
      ) {
        return;
      }
    } else if (asset instanceof AudioAsset) {
      // Make sure no audio layers are using this audio asset
      if (
        this.findLayer((layer) => layer instanceof AudioLayer && layer.getAssetID() === assetID)
      ) {
        return;
      }
    } else {
      throw new Error(`Attempted to remove unsupported asset type: ${asset} `);
    }

    if (this._assetCache.get(assetID) === asset) {
      // This asset could have been replaced with a new asset with the same id,
      // in which case we don't want to delete it
      this._assetCache.delete(assetID);
    }

    const assetIndex = this.rawData.projectManifest.assets.findIndex(
      (asset) => asset?.id === assetID,
    );

    if (assetIndex === -1) {
      return;
    }

    this.rawData.projectManifest.assets.splice(assetIndex, 1);
    this.dispatchEvent('assetRemoved', asset);
  }

  getAssetByID(assetID: string): VideoDescriptorSource | null {
    const cachedAsset = this._assetCache.get(assetID);
    if (cachedAsset) {
      return cachedAsset;
    }

    const asset = this.rawData.projectManifest.assets.find((asset) => asset?.id === assetID);
    if (!asset) {
      return null;
    }
    const assetInstance = makeAsset(asset);
    if (assetInstance) {
      this._assetCache.set(assetID, assetInstance);
    }
    return assetInstance;
  }

  /**
   * Gets the background audio layer for this video descriptor.
   */
  getBackgroundAudioLayer(): AudioLayer | null {
    const backgroundAudioLayerUUID = this.rawData.templateManifest.__backgroundAudioLayerUUID;
    if (!backgroundAudioLayerUUID) {
      return null;
    }
    const backgroundAudioLayer = this.findLayerByUUID(backgroundAudioLayerUUID);
    if (!(backgroundAudioLayer instanceof AudioLayer)) {
      return null;
    }
    return backgroundAudioLayer;
  }

  /**
   * Gets all auxiliary audio layers for this video descriptor.
   */
  getAuxiliaryAudioLayers(): AudioLayer[] {
    const backgroundAudioLayerUUID = this.rawData.templateManifest.__backgroundAudioLayerUUID;
    // Get all audio layers which are not the background audio layer
    return this.filterLayers(
      (layer): layer is AudioLayer =>
        layer instanceof AudioLayer && layer.getUUID() !== backgroundAudioLayerUUID,
    );
  }

  /**
   * Creates a new background audio layer and adds it to this video descriptor's root composition.
   * @param initialAudioAsset - Initial audio asset to use for the layer
   */
  createBackgroundAudioLayer(initialAudioAsset: AudioAsset) {
    // We should only have one background audio layer, so if there is already one, we should remove it.
    const backgroundAudioLayer = this.getBackgroundAudioLayer();
    if (backgroundAudioLayer) {
      this.getRootComposition().removeLayer(backgroundAudioLayer);
    }

    /**
     * The renderer creates the AudioMediaHandler on the layerAdded event, and the editor uses the
     * layerAdded event in a hook to update the background audio layer variable, _but_ when the layer is
     * added it is not yet known to be the background audio layer because the __backgroundAudioLayerUUID
     * is not yet set. So we suppress the layerAdded event on the createAudioLayer call and then manually
     * emit the event after the UUID is assigned.
     */
    const newAudioLayer = AudioLayer.createAudioLayer(initialAudioAsset, this, true);
    this.rawData.templateManifest.__backgroundAudioLayerUUID = newAudioLayer.getUUID();
    this.dispatchEvent('layerAdded', newAudioLayer);
    return newAudioLayer;
  }

  /**
   * Creates a new auxiliary audio layer and adds it to this video descriptor's root composition.
   *
   * @param initialAudioAsset - Initial audio asset to use for the layer
   * @param shouldCopyBackgroundAudioVolumeChanges - Whether to copy volume changes from the background audio layer. This is usually
   *                                                  what we want because we usually want to apply the same volume ducking to both background and auxiliary audio layers.
   */
  createAuxiliaryAudioLayer(
    initialAudioAsset: AudioAsset,
    shouldCopyBackgroundAudioVolumeChanges = true,
  ) {
    const newAudioLayer = AudioLayer.createAudioLayer(initialAudioAsset, this);

    if (shouldCopyBackgroundAudioVolumeChanges) {
      const backgroundAudioLayer = this.getBackgroundAudioLayer();
      if (backgroundAudioLayer) {
        newAudioLayer.setVolumeChanges(backgroundAudioLayer.getVolumeChanges());
      }
    }

    return newAudioLayer;
  }

  getColorReferences(): ColorReference[] {
    return Object.values(this.references.color).sort(
      // Sort by ascending ideal frame number
      (refA, refB) => refA.getIdealDisplayFrame() - refB.getIdealDisplayFrame(),
    );
  }

  getFontReferences(): FontReference[] {
    return Object.values(this.references.font).sort(
      // Sort by ascending ideal frame number
      (refA, refB) => refA.getIdealDisplayFrame() - refB.getIdealDisplayFrame(),
    );
  }

  getInAndOutPointFrames(): { inPoint: number; outPoint: number } {
    return {
      inPoint: this.rawData.projectManifest.ip,
      outPoint: this.rawData.projectManifest.op,
    };
  }

  getInAndOutPointSeconds(): { inPoint: number; outPoint: number } {
    const frameRate = this.getFramerate();
    const { inPoint, outPoint } = this.getInAndOutPointFrames();
    return {
      inPoint: inPoint / frameRate,
      outPoint: outPoint / frameRate,
    };
  }
}
